import React, { useState, useEffect } from 'react';
import {
  Box,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Search,
  Close as CloseIcon,
  EventBusyOutlined as EndIcon,
} from '@mui/icons-material';
import PropTypes from 'prop-types';
import SpinningSpark from '../shared/components/SpinningSpark';

export default function TemplatesFilters({
  filterValues,
  appliedFilters,
  onFilterChange,
  handleDeleteTopic,
  handleDeleteSubtopic,
  topicDropdownOpen,
  setTopicDropdownOpen,
  subtopicDropdownOpen,
  setSubtopicDropdownOpen,
  activeCounts,
}) {
  const { postTypes, topics } = filterValues;
  const [subtopics, setSubtopics] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    onFilterChange(searchQuery);
  }, [searchQuery]);

  return (
    <Box display="flex" gap={2} p={2}>
      <Select
        value={appliedFilters.postType || ''}
        onChange={(e) =>
          onFilterChange(searchQuery, {
            ...appliedFilters,
            postType: e.target.value,
          })
        }
        displayEmpty
        sx={inputStyles}
        renderValue={(selected) =>
          selected
            ? postTypes.find((p) => p.value === selected)?.label
            : 'Post Type'
        }
      >
        <MenuItem value="">All</MenuItem>
        {postTypes.map(({ label, value }) => (
          <MenuItem key={value} value={value}>
            {label}
          </MenuItem>
        ))}
      </Select>

      <Select
        open={topicDropdownOpen}
        onClose={() => setTopicDropdownOpen(false)}
        onOpen={() => setTopicDropdownOpen(true)}
        value={appliedFilters.topic || ''}
        onChange={(e) => {
          const selectedTopic = topics.find(
            (topic) => topic.id === e.target.value
          );
          setSubtopics(selectedTopic?.subtopics || []);
          onFilterChange(searchQuery, {
            ...appliedFilters,
            topic: e.target.value,
            subtopic: '',
          });
        }}
        displayEmpty
        sx={inputStyles}
        renderValue={(selected) =>
          selected ? topics.find((p) => p.id === selected)?.name : 'Topic'
        }
        disabled={appliedFilters.postType === 'pl'}
      >
        <MenuItem value="">All</MenuItem>
        {topics === undefined ? (
          <Box display="flex" justifyContent="center">
            <SpinningSpark isSpinning />
          </Box>
        ) : (
          [...topics]
            .sort((a, b) => {
              const aActive = activeCounts[`topic-${a.id}`] > 0;
              const bActive = activeCounts[`topic-${b.id}`] > 0;
              if (aActive && !bActive) return -1;
              if (!aActive && bActive) return 1;
              return 0;
            })
            .map(({ id, name }) => {
              const hasActive = activeCounts[`topic-${id}`] > 0;
              return (
                <MenuItem key={id} value={id}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    width="100%"
                  >
                    {name}
                    {hasActive && (
                      <IconButton
                        size="small"
                        aria-label="Delete topic"
                        onClick={(event) => handleDeleteTopic(id, event)}
                      >
                        <EndIcon />
                      </IconButton>
                    )}
                  </Box>
                </MenuItem>
              );
            })
        )}
      </Select>

      <Select
        open={subtopicDropdownOpen}
        onClose={() => setSubtopicDropdownOpen(false)}
        onOpen={() => setSubtopicDropdownOpen(true)}
        value={appliedFilters.subtopic || ''}
        onChange={(e) =>
          onFilterChange(searchQuery, {
            ...appliedFilters,
            subtopic: e.target.value,
          })
        }
        displayEmpty
        sx={inputStyles}
        disabled={
          !appliedFilters.topic ||
          subtopics.length === 0 ||
          appliedFilters.postType === 'pl'
        }
        renderValue={(selected) =>
          selected ? subtopics.find((p) => p.id === selected)?.name : 'Subtopic'
        }
      >
        <MenuItem value="">All</MenuItem>
        {[...subtopics]
          .sort((a, b) => {
            const aActive = activeCounts[`subtopic-${a.id}`] > 0;
            const bActive = activeCounts[`subtopic-${b.id}`] > 0;
            if (aActive && !bActive) return -1;
            if (!aActive && bActive) return 1;
            return 0;
          })
          .map(({ id, name }) => {
            const hasActive = activeCounts[`subtopic-${id}`] > 0;
            return (
              <MenuItem key={id} value={id}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  width="100%"
                >
                  {name}
                  {hasActive && (
                    <IconButton
                      size="small"
                      aria-label="Delete subtopic"
                      onClick={(event) => handleDeleteSubtopic(id, event)}
                    >
                      <EndIcon />
                    </IconButton>
                  )}
                </Box>
              </MenuItem>
            );
          })}
      </Select>

      <TextField
        value={searchQuery}
        onChange={(event) => setSearchQuery(event.target.value)}
        placeholder="Search templates"
        variant="outlined"
        size="medium"
        sx={inputStyles}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {searchQuery ? (
                <CloseIcon
                  style={{ cursor: 'pointer', color: '#AEBDCC' }}
                  onClick={() => setSearchQuery('')}
                />
              ) : (
                <Search style={{ color: '#AEBDCC' }} />
              )}
            </InputAdornment>
          ),
        }}
      />
    </Box>
  );
}
const inputStyles = {
  flex: 1,
  minWidth: 150,
  height: 55,
  backgroundColor: '#F6F9FC',
  borderRadius: 2,
  '& fieldset': { border: 'none' },
};

TemplatesFilters.propTypes = {
  filterValues: PropTypes.shape({
    postTypes: PropTypes.arrayOf(PropTypes.shape({})),
    topics: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  appliedFilters: PropTypes.shape({
    postType: PropTypes.string,
    topic: PropTypes.string,
    subtopic: PropTypes.string,
  }).isRequired,
  onFilterChange: PropTypes.func.isRequired,
  handleDeleteTopic: PropTypes.func.isRequired,
  handleDeleteSubtopic: PropTypes.func.isRequired,
  topicDropdownOpen: PropTypes.bool.isRequired,
  setTopicDropdownOpen: PropTypes.func.isRequired,
  subtopicDropdownOpen: PropTypes.bool.isRequired,
  setSubtopicDropdownOpen: PropTypes.func.isRequired,
  activeCounts: PropTypes.objectOf(PropTypes.number).isRequired,
};
